import { useEffect, useState } from "react";
import { Menu, X, ArrowRight } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { NAV_LINKS, BRAND_LOGOS } from "@/lib/site";

export default function Navbar({ onBookDemo }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Always funnel demo intent to /book-demo (single source of truth).
  // Legacy `onBookDemo` prop ignored — kept for backwards compatibility with callers.
  const goToBookDemo = () => navigate("/book-demo");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (href) => {
    if (!href.startsWith("/")) return false;
    if (href === "/") return pathname === "/";
    return pathname === href || pathname.startsWith(href + "/");
  };

  const NavItem = ({ l, onClick }) => {
    const active = isActive(l.href);
    const isRoute = l.href.startsWith("/");
    const Cmp = isRoute ? Link : "a";
    const props = isRoute ? { to: l.href } : { href: l.href };
    return (
      <Cmp
        {...props}
        data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
        onClick={onClick}
        className={`relative px-1 py-1 text-[12.5px] tracking-[0.22em] uppercase transition-colors font-medium ${
          active ? "text-sapphire-glow" : "text-platinum/85 hover:text-white"
        }`}
      >
        {l.label}
        {active && (
          <>
            <span className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-9 h-px"
              style={{ background: "linear-gradient(90deg, transparent, #60A5FA, transparent)", boxShadow: "0 0 8px rgba(96,165,250,0.95)" }} />
            <span className="absolute left-1/2 -translate-x-1/2 -bottom-[14px] w-1.5 h-1.5 rounded-full bg-sapphire-glow"
              style={{ boxShadow: "0 0 10px rgba(96,165,250,1), 0 0 20px rgba(96,165,250,0.7)" }} />
          </>
        )}
      </Cmp>
    );
  };

  return (
    <header data-testid="site-navbar" className="fixed top-3 inset-x-0 z-50 px-4 lg:px-6">
      <div className="mx-auto max-w-[1400px]">
        {/* Glassmorphic pill container */}
        <div
          className={`relative rounded-[26px] transition-all duration-300 ${scrolled ? "shadow-[0_8px_40px_rgba(0,0,0,0.55)]" : ""}`}
          style={{
            background: "linear-gradient(180deg, rgba(15,22,40,0.55) 0%, rgba(10,15,26,0.72) 100%)",
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            border: "1px solid rgba(96,165,250,0.18)",
            boxShadow: "0 0 24px -6px rgba(96,165,250,0.30), inset 0 1px 0 rgba(255,255,255,0.06)",
          }}
        >
          {/* Top + bottom glowing edges (the bright sapphire highlights from the reference) */}
          <div className="pointer-events-none absolute top-0 left-[18%] right-[18%] h-px"
            style={{ background: "linear-gradient(90deg, transparent, rgba(96,165,250,0.85), transparent)", boxShadow: "0 0 12px rgba(96,165,250,0.85)" }} />
          <div className="pointer-events-none absolute bottom-0 left-[22%] right-[22%] h-px"
            style={{ background: "linear-gradient(90deg, transparent, rgba(96,165,250,0.7), transparent)", boxShadow: "0 0 10px rgba(96,165,250,0.7)" }} />

          <div className="relative h-[84px] px-5 lg:px-7 flex items-center justify-between gap-4">
            {/* Logo */}
            <Link to="/" data-testid="nav-logo" className="flex items-center gap-3.5 group shrink-0">
              <div className="relative">
                {/* Soft sapphire halo behind the icon — toned down per founder direction */}
                <div className="absolute inset-[-20%] pointer-events-none"
                  style={{ background: "radial-gradient(closest-side, rgba(96,165,250,0.28), rgba(37,99,235,0.10) 55%, transparent 80%)", filter: "blur(10px)" }} />
                <img src={BRAND_LOGOS.navbar} alt="Esthera RealityOS"
                  className="relative h-[58px] w-[58px] object-contain transition-transform duration-500 group-hover:scale-[1.06] group-hover:rotate-[2deg] logo-breathe"
                  style={{ mixBlendMode: "screen", filter: "drop-shadow(0 0 6px rgba(96,165,250,0.45)) drop-shadow(0 0 2px rgba(147,197,253,0.30)) brightness(1.06) contrast(1.04)" }}
                  draggable="false" />
              </div>
              <div className="leading-[1.05] hidden sm:block text-center">
                <div className="font-display text-[22px] tracking-[0.18em] text-platinum font-semibold"
                  style={{ textShadow: "0 0 16px rgba(229,231,235,0.22)" }}>ESTHERA</div>
                <div className="font-display text-[12px] tracking-[0.58em] text-sapphire-glow mt-[3px] font-semibold pl-[0.58em]"
                  style={{ textShadow: "0 0 8px rgba(96,165,250,0.55)" }}>REALITYOS</div>
              </div>
            </Link>

            {/* Center nav */}
            <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
              {NAV_LINKS.map((l) => <NavItem key={l.label} l={l} />)}
            </nav>

            {/* CTAs */}
            <div className="hidden lg:flex items-center gap-2.5 shrink-0">
              <button data-testid="nav-get-audit" onClick={() => navigate("/get-your-audit-report")}
                className="relative px-4 h-10 rounded-full text-[12px] tracking-[0.15em] uppercase font-medium text-platinum/90 hover:text-white transition-all"
                style={{ background: "rgba(15,22,40,0.4)", border: "1px solid rgba(96,165,250,0.32)", boxShadow: "0 0 14px -2px rgba(96,165,250,0.35), inset 0 0 10px rgba(96,165,250,0.05)" }}>
                Get Audit Report
              </button>
              <button data-testid="nav-book-demo" onClick={goToBookDemo}
                className="relative px-5 h-10 rounded-full text-[12px] tracking-[0.15em] uppercase font-semibold inline-flex items-center gap-2 text-white transition-all hover:scale-[1.02]"
                style={{ background: "linear-gradient(180deg, rgba(96,165,250,0.95), rgba(37,99,235,0.95))", border: "1px solid rgba(147,197,253,0.6)", boxShadow: "0 0 18px rgba(96,165,250,0.55), inset 0 1px 0 rgba(255,255,255,0.25)" }}>
                Book a Demo <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Mobile */}
            <button data-testid="nav-mobile-toggle" onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full text-platinum"
              style={{ background: "rgba(15,22,40,0.5)", border: "1px solid rgba(96,165,250,0.28)" }}
              aria-label="Toggle menu">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {open && (
          <div data-testid="nav-mobile-menu" className="lg:hidden mt-2 rounded-2xl p-5 flex flex-col gap-4"
            style={{ background: "rgba(10,15,26,0.92)", backdropFilter: "blur(18px)", border: "1px solid rgba(96,165,250,0.18)" }}>
            {NAV_LINKS.map((l) => <NavItem key={l.label} l={l} onClick={() => setOpen(false)} />)}
            <button data-testid="nav-mobile-get-audit"
              onClick={() => { setOpen(false); navigate("/get-your-audit-report"); }}
              className="px-4 h-11 rounded-full text-[12px] tracking-[0.15em] uppercase font-medium text-platinum/90"
              style={{ background: "rgba(15,22,40,0.4)", border: "1px solid rgba(96,165,250,0.32)" }}>
              Get Audit Report
            </button>
            <button data-testid="nav-mobile-book-demo"
              onClick={() => { setOpen(false); goToBookDemo(); }}
              className="px-5 h-11 rounded-full text-[12px] tracking-[0.15em] uppercase font-semibold text-white inline-flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(180deg, rgba(96,165,250,0.95), rgba(37,99,235,0.95))", border: "1px solid rgba(147,197,253,0.6)", boxShadow: "0 0 18px rgba(96,165,250,0.55)" }}>
              Book a Demo <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
